export const isLikelyId = (segment) =>
  !!segment && (/^\d+$/.test(segment) || /^[0-9a-fA-F-]{8,}$/.test(segment));

export const shouldNavigateBack = (pathname = "", prevLocation = "/") => {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  const prev = segments[segments.length - 2];
  return prev === prevLocation && isLikelyId(last);
};

/**
 * Navigate back if previous segment looks like `prevLocation` and last segment is an id,
 * otherwise navigate to the provided baseLocation.
 */
export const goBackOrNavigate = (
  navigate,
  pathname,
  prevLocation = "/",
  baseLocation = "/?tab=events"
) => {
  if (shouldNavigateBack(pathname, prevLocation)) {
    navigate(-1);
  } else {
    navigate(baseLocation);
  }
};

// A tab route sits exactly one segment below its shell (`/animal/1/pedigree`).
// Anything deeper is a page of its own — such as the pedigree book entry form,
// which shares the `/animal/:animalId` prefix but renders outside the tabs.
const MAX_TAB_DEPTH = 1;

/**
 * Number of path segments `pathname` sits below `boundary`, or `-1` when it is
 * not below it at all. Segment-aware, so `/animal/12` does not count as being
 * inside `/animal/1`.
 */
export const depthWithin = (pathname = "", boundary = "") => {
  const normalizedBoundary = (boundary ?? "").replace(/\/+$/, "");
  const normalizedPathname = (pathname ?? "").replace(/\/+$/, "");

  if (normalizedBoundary === "") {
    return -1;
  }
  if (normalizedPathname === normalizedBoundary) {
    return 0;
  }
  if (!normalizedPathname.startsWith(`${normalizedBoundary}/`)) {
    return -1;
  }

  return normalizedPathname
    .slice(normalizedBoundary.length)
    .split("/")
    .filter(Boolean).length;
};

/**
 * True when `pathname` is a tab of the shell that owns `boundary` — the shell's
 * own index route or a single segment below it.
 */
export const isTabOf = (pathname = "", boundary = "") => {
  const depth = depthWithin(pathname, boundary);
  return depth >= 0 && depth <= MAX_TAB_DEPTH;
};

// Page-level tab strips (the landing pages, assessments, herd catalog, …) keep
// the pathname and switch content through this search param. Every other search
// param — filters, pagination, `edit`, `asOf` — refines the page in place and
// must not move the user.
const TAB_SEARCH_PARAM = "tab";

const readTabParam = (search = "") =>
  new URLSearchParams(search).get(TAB_SEARCH_PARAM);

/**
 * Decides whether a navigation should scroll back to the top.
 *
 * It should not when the target carries a hash to scroll to, when nothing but
 * an in-place search param changed, or when the previous and next pathname are
 * both tabs of the same registered shell — a route-based tab switch.
 *
 * A change of the `tab` search param does reset: those tab strips replace the
 * whole page body, so the user expects to land at its top.
 *
 * @param {object} params
 * @param {string} params.previousPathname pathname before the navigation
 * @param {string} params.pathname pathname after the navigation
 * @param {string[]} [params.boundaries] active tab shell prefixes
 * @param {string} [params.hash] hash of the target location
 * @param {string} [params.previousSearch] search string before the navigation
 * @param {string} [params.search] search string after the navigation
 * @returns {boolean}
 */
export const shouldResetScroll = ({
  previousPathname,
  pathname,
  boundaries = [],
  hash = "",
  previousSearch = "",
  search = "",
}) => {
  if (hash) {
    return false;
  }

  if (previousPathname === pathname) {
    return readTabParam(previousSearch) !== readTabParam(search);
  }

  return !boundaries.some(
    (boundary) =>
      isTabOf(previousPathname, boundary) && isTabOf(pathname, boundary)
  );
};

// Helpers to persist and derive an "origin" value (where the user entered a flow)
// so that child routes can reliably navigate back to the original entry point.
export const deriveOrigin = (
  locationStateOrigin,
  pathname,
  key = "sale.origin",
  fallback = null
) => {
  if (locationStateOrigin) {
    sessionStorage.setItem(key, locationStateOrigin);
    return locationStateOrigin;
  }

  const stored = sessionStorage.getItem(key);
  if (stored) return stored;

  // Reached only on a cold entry — a deep link or a new tab, where neither the
  // history state nor the session knows where the user came from. `fallback`
  // lets a flow name its own landing page instead of being guessed at.
  const heuristic =
    fallback ?? (pathname?.startsWith("/events") ? "/events" : "/");
  sessionStorage.setItem(key, heuristic);
  return heuristic;
};
